import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Dialog, Box, Grid, Typography, Alert } from '@mui/material'
import { TransferWasteContainerDto, WasteContainerDto, WasteStatus } from '@services/dtos'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import { transferWasteContainer } from '@state/reducers/wasteContainerReducer'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import { wasteContainerService } from '@services/wasteContainerService'
import { MemberDto, MemberService } from '@services/api'
import { getNameWithRole } from '@utils/member.utils'
import { WasteTypeLabels } from '@utils/constants'
import { transferWasteContainerSchema } from '@utils/schemas'
import ServerAutocomplete from '../autocomplete/ServerAutocomplete'
import WasteContainerChips from '../WasteContainerChips'
import { FormHeader } from '../forms/FormHeader'
import { getContainerSubTitles } from '@utils/waste.utils'

type Props = {
  open: boolean
  container: WasteContainerDto
  onClose: () => void
}

export function TransferWasteContainerDialog({ open, container, onClose }: Props) {
  const dispatch = useAppDispatch()
  const user = useAppSelector(({ auth }) => auth.user)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [targetContainer, setTargetContainer] = useState<WasteContainerDto | null>(null)
  const [noCompatibleContainers, setNoCompatibleContainers] = useState<boolean | null>(null)

  const defaultMember = user && 'role' in user ? user as unknown as MemberDto : null

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<TransferWasteContainerDto>({
    resolver: yupResolver(transferWasteContainerSchema),
    defaultValues: {
      transferredBy: defaultMember?.id ?? undefined,
    },
  })

  const fetchOpenContainers = async (search: string) => {
    const page = await wasteContainerService.getAll({
      search,
      status: WasteStatus.OPEN,
      type: container.type,
      isotopeIds: container.activeIsotopes.length > 0 ? container.activeIsotopes.map((i) => i.id).join(',') : undefined,
      limit: 20,
      page: 0,
    })
    const results = (page?.datas ?? []).filter((c) => c.id !== container.id)
    setNoCompatibleContainers(results.length === 0)
    return results
  }

  const fetchMembers = async (search: string) => {
    const response = await MemberService.findAll({ search, limit: 20 })
    return response?.datas || []
  }

  const onSubmit = (data: TransferWasteContainerDto) => {
    setIsSubmitting(true)
    dispatch(transferWasteContainer({ id: container.id, dto: data }))
      .unwrap()
      .then(() => {
        dispatch(enqueueSnackbar({ message: 'Contenu transvasé avec succès', options: { variant: 'success' } }))
        onClose()
      })
      .catch(() =>
        dispatch(enqueueSnackbar({ message: 'Une erreur est survenue lors du transvasement', options: { variant: 'error' } })),
      )
      .finally(() => setIsSubmitting(false))
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <div className="flex w-full h-full p-4">
        <form
          onSubmit={(e) => { e.stopPropagation(); handleSubmit(onSubmit)(e) }}
          className="w-full"
        >
          <FormHeader
            isSubmitting={isSubmitting}
            onClose={onClose}
            title="Transvaser"
            subTitles={getContainerSubTitles(container)}
            canSubmit={noCompatibleContainers !== true}
          />

          <Grid container rowSpacing={2} mt={1}>
            <Grid item xs={12}>
              <Controller
                name="targetContainerId"
                control={control}
                render={({ field: { onChange } }) => (
                  <>
                    <ServerAutocomplete<WasteContainerDto>
                      autoFetchOnMount
                      disabled={noCompatibleContainers === true}
                      value={targetContainer}
                      fetchService={fetchOpenContainers}
                      onChange={(c) => {
                        onChange(c?.id ?? null)
                        setTargetContainer(c ?? null)
                      }}
                      getValue={(c) => c?.id ?? null}
                      getOptionLabel={(value) => {
                        const c = value as WasteContainerDto
                        return c ? c.label : ''
                      }}
                      renderOption={(props, c) => (
                        <li {...props} key={c.id}>
                          <Box sx={{ py: 0.25, width: '100%' }}>
                            <Typography variant="body2" fontWeight={400}>{c.label}</Typography>
                            <WasteContainerChips container={c} />
                          </Box>
                        </li>
                      )}
                      placeholder="Rechercher un conteneur ouvert compatible..."
                      label="Conteneur de destination"
                      minSearchLength={0}
                      helperText={errors.targetContainerId?.message}
                      error={!!errors.targetContainerId}
                    />
                    {targetContainer && (
                      <Box mt={1}>
                        <WasteContainerChips container={targetContainer} />
                      </Box>
                    )}
                  </>
                )}
              />
              {noCompatibleContainers === true ? (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    Aucun conteneur compatible disponible. Les conteneurs de destination doivent être ouverts, de type {WasteTypeLabels[container.type]}
                    {container.activeIsotopes.length > 0 && ` et accepter les isotopes ${container.activeIsotopes.map((i) => `${i.name} (${i.code})`).join(', ')}`}.
                  </Typography>
                </Alert>
              ) : (
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    Conteneurs compatibles : type {WasteTypeLabels[container.type]}
                    {container.activeIsotopes.length > 0 && ` · isotopes ${container.activeIsotopes.map((i) => `${i.name} (${i.code})`).join(', ')}`}
                  </Typography>
                </Alert>
              )}
            </Grid>

            <Grid item xs={12}>
              <ServerAutocomplete<MemberDto>
                defaultValue={defaultMember}
                initialOptions={defaultMember ? [defaultMember] : []}
                value={defaultMember ?? null}
                disabled={noCompatibleContainers === true}
                fetchService={fetchMembers}
                onChange={(value: MemberDto | null) => setValue('transferredBy', value?.id)}
                getValue={(value) => value?.id?.toString() || null}
                getOptionLabel={(value) => getNameWithRole(value as MemberDto)}
                debounceMs={300}
                placeholder="Rechercher un membre..."
                label="Transvasé par"
                helperText={errors.transferredBy?.message}
                error={!!errors.transferredBy}
                minSearchLength={0}
              />
            </Grid>
          </Grid>
        </form>
      </div>
    </Dialog>
  )
}
